import { articleDetailQuery } from "../article/hooks/useArticleData";
import {
  IconArrowRight,
  IconCheck,
  IconLoader2,
  IconLock,
  IconPresentationAnalytics,
  IconX,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { z } from "zod";
import { cn } from "../../lib/utils";
import {
  isDesktopShell,
  useIsXlViewport,
} from "../../common/hooks/useIsDesktopView";
import { DesktopBreadcrumb } from "../../components/app/desktopTopNav/DesktopBreadcrumb";
import { DesktopTopNav } from "../../components/app/desktopTopNav/DesktopTopNav";
import ContentHeader from "../../components/app/headers/ContentHeader";
import BottomPanel from "../../components/app/panels/BottomPanel";
import KeyboardAvoidingView from "../../components/layout/KeyboardAvoidingView";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import SkeletonPublicationCard from "../../components/skeleton/SkeletonPublicationCard";
import { Button } from "../../components/ui/Button";
import { Logo } from "../../components/ui/Logo";
import { PublicationCard } from "../../components/ui/PublicationCard";
import { Select } from "../../components/ui/Select";
import { Tag } from "../../components/ui/Tag";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { useLocationState } from "../../domains/app/hooks/useLocationState";
import { usePageLinks } from "../../domains/app/hooks/usePageLinks";
import { getArticleTypeVariant } from "../../domains/app/utils/getArticleTypeText";
import { renderDateRelative } from "../../domains/app/utils/renderDate";
import { getRoomName } from "../../domains/room/hooks/useRoomName";
import { getRoomLogoUrl } from "../../domains/room/utils/getRoomLogoUrl";
import { useApiQuery, type Api } from "../../services/api";
import {
  gaEventVisualAbstractCtaShown,
  gaEventVisualAbstractUnavailable,
} from "../../tools/analytics/domains/article.analytics";
import { SearchInput } from "../search/components/SearchInput";
import type { VisualAbstractList } from "./useArticleVisualAbstracts";
import { useTrackOnMount } from "../../hooks/use-track-on-mount";
import { useRequestVisualAbstract } from "./useRequestVisualAbstract";
import {
  sourceLine,
  VisualAbstractArticleRecap,
} from "./VisualAbstractArticleRecap";
import { authorLine } from "../../domains/article/utils/authorLine";

// The Search page's own threshold, for the same reason: one- and two-letter
// queries match most of the corpus on trigram and drive the worst latency.
const SEARCH_MIN_LENGTH = 3;

/**
 * The screen's URL, validated at the boundary. `room` goes straight into API
 * queries that expect an ObjectId, so a malformed one fails here, loudly, rather
 * than as an opaque 400 from the candidate routes.
 */
const createSearchParamsSchema = z.object({
  room: z
    .string()
    .regex(/^[a-f\d]{24}$/i)
    .nullable(),
  value: z.string().nullable(),
  article: z.string().min(1).nullable(),
});

/**
 * Who opened this screen. The library's "+" says so, which lets a finished
 * request step BACK to the library instead of stacking a second copy of it; a
 * reload or a deep link carries no state and replaces the entry instead.
 */
const createLocationStateSchema = z.object({
  from: z.enum(["library"]).nullable().default(null),
});

type Candidate =
  Api["get"]["/webapp/private/visual-abstracts/candidates"]["output"]["body"]["docs"][number];
type Article = Api["get"]["/webapp/public/articles/:slug"]["output"]["body"];
type IneligibleReason = Extract<
  VisualAbstractList["eligibility"],
  { state: "ineligible" }
>["reason"];

/**
 * Create a visual abstract from the library: pick a room, find the paper, then
 * confirm in a sheet.
 *
 * The whole screen lives in its URL (`room`, `value`, `article`), the way the
 * Search page keeps `value` and `scope`. That is what lets "View article" leave
 * for the article page and come back to the same room, the same results and the
 * same open sheet: the article page's Back is a plain `history.goBack()`, and
 * only the URL survives the round trip.
 *
 * The room is chosen BEFORE the search rather than in the sheet. The search
 * applies it as a strict room filter, so every result belongs to that room and
 * the server's "article is in the selected room" check cannot refuse the
 * request. A room picked after the paper would have no such guarantee.
 */
export default function VisualAbstractCreatePage(): React.ReactElement {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  // Below the 1280px window the side panel beside the sidebar would leave the
  // form a strip: the confirmation opens as the bottom sheet there.
  const isXlViewport = useIsXlViewport();
  const { from } = useLocationState(createLocationStateSchema);
  const params = new URLSearchParams(location.search);
  const {
    room: roomId,
    value: rawValue,
    article: articleSlug,
  } = createSearchParamsSchema.parse({
    room: params.get("room"),
    value: params.get("value"),
    article: params.get("article"),
  });
  const searchValue = rawValue === null ? "" : rawValue;

  function replaceParams(mutate: (next: URLSearchParams) => void): void {
    const next = new URLSearchParams(location.search);
    mutate(next);
    const qs = next.toString();
    // The state rides along: it is how a finished request knows it may step
    // back to the library, and `history.replace` would otherwise drop it.
    history.replace({
      pathname: location.pathname,
      search: qs.length === 0 ? "" : `?${qs}`,
      state: { from },
    });
  }

  function returnToLibrary(): void {
    if (from === "library") {
      history.goBack();
      return;
    }
    history.replace("/visual-abstracts");
  }

  // A request answers after the reader may have left: on desktop the Back
  // chevron and the breadcrumb stay clickable beside the panel. Stepping back
  // then would apply to someone else's history entry, so the request only
  // navigates while the reader is still on the very entry they clicked from.
  // The URL cannot tell: a new creation visit can show the same path and even
  // the same article. Every navigation, `replace` included, mints a new key.
  function returnToLibraryIfStillHere(entryKey: string | null): void {
    const currentKey =
      history.location.key === undefined ? null : history.location.key;
    if (currentKey !== entryKey) return;
    returnToLibrary();
  }

  function selectArticle(slug: string): void {
    replaceParams((next) => {
      next.set("article", slug);
    });
  }

  const form = (layout: FormLayout): React.ReactElement => (
    <CreateForm
      roomId={roomId}
      searchValue={searchValue}
      layout={layout}
      onRoomChange={(nextRoomId) => {
        replaceParams((next) => {
          if (nextRoomId === null) next.delete("room");
          else next.set("room", nextRoomId);
          // The selected article belongs to the previous room's results; the
          // desktop panel stays open beside the form and would check it
          // against the new room.
          next.delete("article");
        });
      }}
      onSearch={(nextValue) => {
        replaceParams((next) => {
          if (nextValue.length === 0) next.delete("value");
          else next.set("value", nextValue);
        });
      }}
      onSelect={selectArticle}
    />
  );
  const closeSheet = (): void => {
    replaceParams((next) => {
      next.delete("article");
    });
  };

  // Desktop (Figma "D2.va-create"): the page's own top bar, a centred column,
  // and the confirmation in a panel on the right instead of a bottom sheet. The
  // column stays usable while the panel is open, so picking another result
  // swaps the panel's article rather than closing it first.
  if (isDesktopShell())
    return (
      <div className="flex h-full overflow-hidden bg-ds-cream">
        <div className="flex min-w-0 flex-1 flex-col">
          <DesktopTopNav
            context={
              <DesktopBreadcrumb
                // Back returns to the library, which the first crumb names.
                back={{ kind: "first-crumb", onBack: returnToLibrary }}
                trail={[
                  { label: t("Visual Abstracts"), to: "/visual-abstracts" },
                ]}
                current={t("New visual abstract")}
              />
            }
            trailing={{ kind: "actions", content: null }}
            // The page's own colour shows through the bar, as in the design.
            surface="default"
            className="bg-transparent"
          />
          <main className="min-h-0 flex-1 overflow-y-auto px-6 pt-8 pb-12">
            <div className="mx-auto flex w-full max-w-156 flex-col gap-4">
              {form({
                shell: "desktop",
                selectedSlug: articleSlug,
                onSearchAllArticles: () => {
                  replaceParams((next) => {
                    next.delete("room");
                    // The selected article was picked in that room: offered
                    // without it, its poster would change scope or branding.
                    next.delete("article");
                  });
                },
              })}
            </div>
          </main>
        </div>
        {!isXlViewport ? (
          <CreateSheet
            frame="bottom-sheet"
            articleSlug={articleSlug}
            roomId={roomId}
            onClose={closeSheet}
            onRequested={returnToLibraryIfStillHere}
          />
        ) : articleSlug === null ? null : (
          <aside className="flex w-gpt-context-panel shrink-0 flex-col py-4 pr-6">
            <CreateSheet
              frame="side-panel"
              articleSlug={articleSlug}
              roomId={roomId}
              onClose={closeSheet}
              onRequested={returnToLibraryIfStillHere}
            />
          </aside>
        )}
      </div>
    );

  return (
    <ScrollableLayout
      background="var(--color-ds-cream)"
      headerClassName="bg-white/90 backdrop-blur-md"
      header={
        <ContentHeader
          // Same rule as a finished request: back to the library entry that
          // opened this screen, or to the library itself on a cold entry, where
          // `goBack()` could leave the app.
          onBack={returnToLibrary}
          title={t("New visual abstract")}
          transparent
        />
      }
      footer={null}
    >
      <KeyboardAvoidingView behavior="padding">
        <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 px-4 pt-3 pb-8">
          {form({ shell: "mobile" })}
        </div>
      </KeyboardAvoidingView>

      <CreateSheet
        frame="bottom-sheet"
        articleSlug={articleSlug}
        roomId={roomId}
        onClose={closeSheet}
        onRequested={returnToLibraryIfStillHere}
      />
    </ScrollableLayout>
  );
}

/**
 * How the form lays out its results. The desktop rows are compact and mark the
 * article the side panel is showing; a room search that finds nothing offers
 * the same query on every article.
 */
type FormLayout =
  | { shell: "mobile" }
  | {
      shell: "desktop";
      selectedSlug: string | null;
      onSearchAllArticles: () => void;
    };

function CreateForm({
  roomId,
  searchValue,
  layout,
  onRoomChange,
  onSearch,
  onSelect,
}: {
  roomId: string | null;
  searchValue: string;
  layout: FormLayout;
  onRoomChange: (roomId: string | null) => void;
  onSearch: (value: string) => void;
  onSelect: (slug: string) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const isSearching = searchValue.length >= SEARCH_MIN_LENGTH;
  return (
    <>
      <RoomField roomId={roomId} shell={layout.shell} onChange={onRoomChange} />
      <SearchInput
        value={searchValue}
        placeholder={t("Title, DOI, author or topic")}
        autoFocus
        onSearch={onSearch}
      />
      {isSearching ? (
        <SearchArticleList
          searchValue={searchValue}
          roomId={roomId}
          layout={layout}
          onSelect={onSelect}
        />
      ) : (
        <>
          <div className="flex items-start gap-2 rounded-2xl bg-ds-mint-50 px-3 py-2.5">
            <IconPresentationAnalytics className="mt-0.5 size-4 shrink-0 text-ds-mint-800" />
            <p className="m-0 font-dm-sans text-ui-note font-medium text-ds-mint-800">
              {layout.shell === "desktop"
                ? t(
                    "Only open-access articles of supported types can become a visual abstract. It needs the full text."
                  )
                : t(
                    "A visual abstract is built from the full text, so it is available for open-access articles."
                  )}
            </p>
          </div>
          <RecentlyReadList
            roomId={roomId}
            layout={layout}
            onSelect={onSelect}
          />
        </>
      )}
    </>
  );
}

type RoomOption = {
  label: string;
  value: string;
  roomId: string | null;
  logoUrl: string | null;
  /** What picking it changes: which articles the search covers, and whose mark the poster wears. */
  description: string;
};

/**
 * Juisci, or one of the reader's rooms. Absent for a reader in no room: a
 * select with a single option is a question with one answer.
 */
function RoomField({
  roomId,
  shell,
  onChange,
}: {
  roomId: string | null;
  shell: FormLayout["shell"];
  onChange: (roomId: string | null) => void;
}): React.ReactElement | null {
  const { t } = useTranslation();
  const { roomTextLang } = useActiveLang();
  // The rooms menu's own query and cache entry.
  const roomsQuery = useApiQuery("/webapp/private/rooms", { durable: true });
  if (roomsQuery.state !== "success" || roomsQuery.body.rooms.length === 0)
    return null;

  const juisciOption: RoomOption = {
    label: "Juisci",
    value: "juisci",
    roomId: null,
    logoUrl: null,
    description: t("All articles · Juisci branding"),
  };
  const options: RoomOption[] = [
    juisciOption,
    ...roomsQuery.body.rooms.map((room) => ({
      label: getRoomName(room, roomTextLang),
      value: room.id,
      roomId: room.id,
      logoUrl: getRoomLogoUrl(room),
      description: t("Room articles · room logo"),
    })),
  ];

  return (
    <label className="flex flex-col gap-1.5">
      <span className="font-dm-sans text-ui-note font-semibold text-ds-slate-500">
        {t("Room")}
      </span>
      <Select
        appearance="field"
        footerAction={null}
        searchOnlyOptions={null}
        value={roomId === null ? juisciOption.value : roomId}
        options={options}
        onChange={(option) => {
          onChange(option.roomId);
        }}
        renderOption={(option, isSelected) => (
          <span
            className={cn(
              "flex items-center gap-2.5",
              // Room for the check mark at the row's right edge.
              shell === "desktop" ? "min-w-0 flex-1" : null
            )}
          >
            {option.logoUrl === null ? (
              <Logo variant="app-icon" className="size-6 rounded-lg" />
            ) : (
              <img
                alt=""
                src={option.logoUrl}
                className="size-6 rounded-lg bg-white object-contain"
              />
            )}
            <span
              className={cn(
                "flex min-w-0 flex-col",
                shell === "desktop" ? "flex-1" : null
              )}
            >
              <span className="truncate">{option.label}</span>
              <span className="truncate text-ui-caption text-ds-slate-500">
                {option.description}
              </span>
            </span>
            {shell === "desktop" && isSelected ? (
              <IconCheck className="size-4.5 shrink-0 text-ds-mint-800" />
            ) : null}
          </span>
        )}
      />
    </label>
  );
}

/**
 * The reader's history, cut down to what a poster can be made of in the
 * selected room. The server applies the eligibility rule, so every row opens a
 * sheet that offers a generation.
 */
function RecentlyReadList({
  roomId,
  layout,
  onSelect,
}: {
  roomId: string | null;
  layout: FormLayout;
  onSelect: (slug: string) => void;
}): React.ReactElement | null {
  const { t } = useTranslation();
  const query = useApiQuery(
    "/webapp/private/visual-abstracts/candidates/recently-read",
    { query: { roomId } }
  );
  if (query.state === "pending") return <ArticleRowsSkeleton />;
  // An unavailable history is not worth an error here: the search field above
  // is the screen's real entry point, and this list only saves a few keystrokes.
  if (query.state === "error" || query.body.docs.length === 0) return null;

  return (
    <section className="flex flex-col gap-2">
      <h2 className="m-0 font-dm-sans text-ui-note font-semibold tracking-wide text-ds-slate-500 uppercase">
        {t("Recently read")}
      </h2>
      <ArticleRows
        articles={query.body.docs}
        layout={layout}
        onSelect={onSelect}
      />
    </section>
  );
}

function SearchArticleList({
  searchValue,
  roomId,
  layout,
  onSelect,
}: {
  searchValue: string;
  roomId: string | null;
  layout: FormLayout;
  onSelect: (slug: string) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const { roomTextLang } = useActiveLang();
  // Eligible articles only: the server runs the search, then the eligibility
  // rule on the most relevant candidates. No pagination, by design: a list
  // filtered after the search cannot page by offset, so the reader refines the
  // query instead.
  // No `keepPreviousData`: across a room change the previous list belongs to
  // the other room, and a tap on one of its rows would open a sheet the server
  // refuses. A skeleton while the new list loads is the honest state.
  const query = useApiQuery("/webapp/private/visual-abstracts/candidates", {
    query: { search: searchValue, roomId },
  });
  // The Room field's own query and cache entry, read here for the room's name.
  const roomsQuery = useApiQuery(
    "/webapp/private/rooms",
    roomId === null ? false : { durable: true }
  );
  const roomName =
    roomId !== null && roomsQuery.state === "success"
      ? selectedRoomName(roomsQuery.body.rooms, roomId, roomTextLang)
      : null;
  if (query.state === "pending") return <ArticleRowsSkeleton />;
  if (query.state === "error")
    return (
      <p className="m-0 py-8 text-center font-dm-sans text-ui-note text-ds-slate-500">
        {t("Something went wrong. Please try again.")}
      </p>
    );
  const articles = query.body.docs;
  if (articles.length === 0 && layout.shell === "desktop" && roomName !== null)
    return (
      <div className="flex flex-col items-center gap-2 py-12 text-center">
        <p className="m-0 font-dm-sans text-ui-highlight font-semibold text-ds-slate-950">
          {t("No article in {{roomName}} matches “{{searchValue}}”", {
            roomName,
            searchValue,
          })}
        </p>
        <p className="m-0 font-dm-sans text-ui-body text-ds-slate-500">
          {t("We kept your search. Try it on all Juisci articles.")}
        </p>
        <Button
          variant="outline"
          size="md"
          type="button"
          className="mt-4"
          onClick={layout.onSearchAllArticles}
        >
          {t("Search all articles")}
        </Button>
      </div>
    );
  if (articles.length === 0)
    return (
      <p className="m-0 py-8 text-center font-dm-sans text-ui-note text-ds-slate-500">
        {t("No article eligible for a visual abstract matches this search.")}
      </p>
    );

  return (
    <section className="flex flex-col gap-2">
      <h2 className="m-0 font-dm-sans text-ui-note font-semibold tracking-wide text-ds-slate-500 uppercase">
        {roomName === null
          ? t("{{count}} articles", { count: articles.length })
          : t("{{count}} articles in {{roomName}}", {
              count: articles.length,
              roomName,
            })}
      </h2>
      <ArticleRows articles={articles} layout={layout} onSelect={onSelect} />
    </section>
  );
}

function selectedRoomName(
  rooms: Api["get"]["/webapp/private/rooms"]["output"]["body"]["rooms"],
  roomId: string,
  language: ReturnType<typeof useActiveLang>["roomTextLang"]
): string | null {
  const room = rooms.find((candidate) => candidate.id === roomId);
  // A room id in the URL the reader is no longer a member of: the heading
  // falls back to the plain count, and the server refuses the room anyway.
  return room === undefined ? null : getRoomName(room, language);
}

/**
 * The Search page's publication card, tapped to open the sheet rather than the
 * article. Every row is an eligible article: the candidate routes filter before
 * they answer, so the reader is never offered a paper the sheet would refuse.
 */
function ArticleRows({
  articles,
  layout,
  onSelect,
}: {
  articles: Candidate[];
  layout: FormLayout;
  onSelect: (slug: string) => void;
}): React.ReactElement {
  if (layout.shell === "desktop")
    return (
      <DesktopArticleRows
        articles={articles}
        selectedSlug={layout.selectedSlug}
        onSelect={onSelect}
      />
    );
  return (
    <ul className="m-0 flex list-none flex-col divide-y divide-ds-black/6 p-0">
      {articles.map((article) => (
        <li key={article.id} className="py-4 first:pt-0 last:pb-0">
          <PublicationCard
            isInPlayer={false}
            title={article.title}
            journalName={article.journal === null ? null : article.journal.name}
            author={authorLine(article.primary_author, article.authors)}
            date={
              article.publication_date === null
                ? ""
                : new Date(article.publication_date).getUTCFullYear().toString()
            }
            meta={
              article.article_type === null ? "Article" : article.article_type
            }
            metaVariant={getArticleTypeVariant(article.article_type)}
            access={article.isOpenAccess === true ? "open" : "closed"}
            imageUrl={null}
            description={null}
            recommendation={null}
            isNew={false}
            isBookmarked={false}
            onBookmark={null}
            onShare={null}
            onClick={() => {
              onSelect(article.slug);
            }}
            onJournalClick={null}
            onRecommendationClick={null}
            // The card paints white for the white feeds it was built for; on
            // this cream screen that reads as a box around every result.
            className="bg-transparent"
          />
        </li>
      ))}
    </ul>
  );
}

/**
 * The desktop result row (Figma "ArticleRow"): source line, access tag and
 * title, no card chrome. The row whose article the side panel is showing stays
 * marked, so the reader sees which result they are confirming.
 */
function DesktopArticleRows({
  articles,
  selectedSlug,
  onSelect,
}: {
  articles: Candidate[];
  selectedSlug: string | null;
  onSelect: (slug: string) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  return (
    <ul className="m-0 flex list-none flex-col p-0">
      {articles.map((article) => {
        const source = sourceLine(article);
        return (
          <li key={article.id}>
            <button
              type="button"
              aria-current={article.slug === selectedSlug}
              className={cn(
                "flex w-full cursor-pointer flex-col items-start gap-1 border-none bg-transparent text-left",
                article.slug === selectedSlug
                  ? "rounded-lg bg-ds-mint-50 p-3"
                  : "border-b border-solid border-ds-slate-100 px-0 py-3 hover:bg-ds-slate-50"
              )}
              onClick={() => {
                onSelect(article.slug);
              }}
            >
              <span className="flex flex-wrap items-center gap-2">
                {source === null ? null : (
                  <span className="font-dm-sans text-xs font-medium text-ds-slate-500">
                    {source}
                  </span>
                )}
                {article.isOpenAccess === true ? (
                  <Tag size="sm" variant="mint" state="idle" icon={null}>
                    {t("Open access")}
                  </Tag>
                ) : (
                  <Tag
                    size="sm"
                    variant="slate"
                    state="idle"
                    icon={<IconLock className="size-3" stroke={1.5} />}
                  >
                    {t("Closed access")}
                  </Tag>
                )}
              </span>
              <span className="font-dm-sans text-ui-body font-semibold text-ds-slate-950">
                {article.title}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * The rows above, loading: the publication card's own silhouette, without the
 * hero image the rows never pass.
 */
function ArticleRowsSkeleton(): React.ReactElement {
  return (
    <ul className="m-0 flex list-none flex-col divide-y divide-ds-black/6 p-0">
      {Array.from({ length: 4 }, (_, index) => (
        <li key={index} className="py-4 first:pt-0 last:pb-0">
          <SkeletonPublicationCard showDescription={false} showImage={false} />
        </li>
      ))}
    </ul>
  );
}

type CreateOffer =
  | { kind: "ineligible"; reason: IneligibleReason }
  | { kind: "generate" }
  | { kind: "generating" }
  | { kind: "ready"; artifactId: string; createdAt: string };

/**
 * What the sheet offers, from the API's own verdict. The client never
 * re-derives eligibility; it only reads which of the four answers it got.
 *
 * A FAILED latest run offers a new one: the server only dedupes on runs that
 * are queued, generating or ready, so a retry is a legitimate request.
 */
export function createOffer(body: VisualAbstractList): CreateOffer {
  if (body.eligibility.state === "ineligible")
    return { kind: "ineligible", reason: body.eligibility.reason };
  const latest = body.artifacts.at(0);
  if (latest === undefined) return { kind: "generate" };
  switch (latest.status) {
    case "queued":
    case "generating":
      return { kind: "generating" };
    case "ready":
      return {
        kind: "ready",
        artifactId: latest.id,
        createdAt: latest.createdAt,
      };
    case "failed":
      return { kind: "generate" };
    default:
      throw new Error(latest satisfies never);
  }
}

/**
 * Where the confirmation sits: a bottom sheet on the phone, a panel beside the
 * results on desktop. The panel keeps its action at the bottom of its height.
 */
type SheetFrame = "bottom-sheet" | "side-panel";

/**
 * The confirmation sheet, open while the URL names an article.
 *
 * It owns both queries because its title depends on their answer: the sheet is
 * "New visual abstract" while there is something to generate, and says "Not
 * available" or "Already generated" when there is not.
 */
function CreateSheet({
  frame,
  articleSlug,
  roomId,
  onClose,
  onRequested,
}: {
  frame: SheetFrame;
  articleSlug: string | null;
  roomId: string | null;
  onClose: () => void;
  onRequested: (entryKey: string | null) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  // The article page's own query: the sheet warms the cache "View article"
  // lands on.
  const articleQuery = useApiQuery(
    "/webapp/public/articles/:slug",
    articleSlug === null
      ? false
      : { param: { slug: articleSlug }, query: articleDetailQuery(roomId) }
  );
  // The key the article page's pill, the chat card and the header sheet use.
  const listQuery = useApiQuery(
    "/webapp/private/articles/:id/visual-abstracts",
    articleQuery.state === "success"
      ? { param: { id: articleQuery.body.id }, query: { roomId } }
      : false
  );
  const title = useSheetTitle(
    listQuery.state === "success" ? createOffer(listQuery.body) : null
  );

  const body =
    articleQuery.state === "idle" ? null : articleQuery.state === "pending" ? (
      <SheetLoading />
    ) : articleQuery.state === "error" ? (
      <p
        className={cn(
          "m-0 font-dm-sans text-ui-note text-ds-slate-500",
          frame === "bottom-sheet" ? "px-5 pb-5" : null
        )}
      >
        {t("This content is no longer available.")}
      </p>
    ) : (
      <div
        className={
          frame === "side-panel"
            ? "flex min-h-0 flex-1 flex-col gap-5"
            : "flex flex-col gap-4 px-5 pt-2 pb-5"
        }
      >
        <SheetArticle
          frame={frame}
          article={articleQuery.body}
          roomId={roomId}
        />
        {listQuery.state === "idle" || listQuery.state === "pending" ? (
          <SheetLoading />
        ) : listQuery.state === "error" ? (
          <p className="m-0 font-dm-sans text-ui-note text-ds-slate-500">
            {t("Unable to generate the visual abstract")}
          </p>
        ) : (
          <OfferBody
            frame={frame}
            offer={createOffer(listQuery.body)}
            article={articleQuery.body}
            roomId={roomId}
            onChooseAnother={onClose}
            onRequested={onRequested}
          />
        )}
      </div>
    );

  switch (frame) {
    case "bottom-sheet":
      return (
        <BottomPanel
          isOpen={articleSlug !== null}
          onClose={onClose}
          title={title}
          displayCloseButton
        >
          {body}
        </BottomPanel>
      );
    case "side-panel":
      return (
        <section
          aria-label={title}
          className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto rounded-2xl bg-ds-white px-6 pt-5 pb-6 shadow-xs"
        >
          <div className="flex items-center gap-3">
            <h2 className="m-0 min-w-0 flex-1 font-dm-sans text-publication-lg font-semibold text-ds-slate-950">
              {title}
            </h2>
            <button
              type="button"
              aria-label={t("Close")}
              className="flex shrink-0 cursor-pointer items-center justify-center rounded-full border-none bg-ds-slate-100 p-1.5 text-ds-slate-800 hover:bg-ds-slate-200"
              onClick={onClose}
            >
              <IconX className="size-5" stroke={1.5} />
            </button>
          </div>
          {body}
        </section>
      );
    default:
      throw new Error(frame satisfies never);
  }
}

/** Each title a literal key, so the i18n scanner sees all three. */
function useSheetTitle(offer: CreateOffer | null): string {
  const { t } = useTranslation();
  if (offer === null) return t("New visual abstract");
  switch (offer.kind) {
    case "generate":
    case "generating":
      return t("New visual abstract");
    case "ready":
      return t("Already generated");
    case "ineligible":
      return t("Not available");
    default:
      throw new Error(offer satisfies never);
  }
}

function SheetArticle({
  frame,
  article,
  roomId,
}: {
  frame: SheetFrame;
  article: Article;
  roomId: string | null;
}): React.ReactElement {
  const { t } = useTranslation();
  const history = useHistory();
  const { articleLink } = usePageLinks(roomId);
  if (frame === "side-panel")
    return (
      <VisualAbstractArticleRecap
        article={article}
        roomId={roomId}
        authors={null}
      />
    );
  return (
    <div className="flex flex-col gap-1.5 rounded-2xl border border-solid border-ds-slate-100 bg-ds-cream px-4 py-3.5">
      <div className="flex flex-wrap items-center gap-2">
        {article.journal === null ? null : (
          <span className="font-dm-sans text-ui-caption font-medium text-ds-slate-500">
            {article.journal.name}
          </span>
        )}
        {article.isOpenAccess === true ? (
          <Tag size="sm" variant="mint" state="idle" icon={null}>
            {t("Open access")}
          </Tag>
        ) : (
          <Tag
            size="sm"
            variant="slate"
            state="idle"
            icon={<IconLock className="size-3" stroke={1.5} />}
          >
            {t("Closed access")}
          </Tag>
        )}
      </div>
      <p className="m-0 line-clamp-3 font-dm-sans text-ui-body font-semibold text-ds-black">
        {article.title}
      </p>
      {/* A push, not a replace: the article page's Back is `goBack()`, and it
          has to land on THIS entry, whose URL still names the article and so
          reopens the sheet. */}
      <button
        type="button"
        className="flex w-fit cursor-pointer items-center gap-1 border-none bg-transparent p-0 pt-1 font-dm-sans text-ui-note font-semibold text-ds-mint-800"
        onClick={() => {
          history.push(articleLink(article));
        }}
      >
        {t("View article")}
        <IconArrowRight className="size-3.5" />
      </button>
    </div>
  );
}

function OfferBody({
  frame,
  offer,
  article,
  roomId,
  onChooseAnother,
  onRequested,
}: {
  frame: SheetFrame;
  offer: CreateOffer;
  article: Article;
  roomId: string | null;
  onChooseAnother: () => void;
  onRequested: (entryKey: string | null) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const history = useHistory();
  switch (offer.kind) {
    case "generate":
      return (
        <GenerateOffer
          frame={frame}
          article={article}
          roomId={roomId}
          onRequested={onRequested}
        />
      );
    case "generating":
      return (
        <>
          <SheetFact
            frame={frame}
            icon={<IconLoader2 className="size-4.5 animate-spin" />}
          >
            {t("A visual abstract of this article is being generated.")}
          </SheetFact>
          <Button
            variant="primary"
            size={frame === "side-panel" ? "lg" : "md"}
            type="button"
            className="mt-auto"
            disabled
          >
            {t("Generating…")}
          </Button>
        </>
      );
    case "ready":
      return (
        <>
          <SheetFact
            frame={frame}
            icon={<IconPresentationAnalytics className="size-4.5" />}
          >
            {t(
              "You generated a visual abstract of this article {{date}}. It is in your visual abstracts.",
              { date: renderDateRelative(offer.createdAt) }
            )}
          </SheetFact>
          <Button
            variant="primary"
            size={frame === "side-panel" ? "lg" : "md"}
            type="button"
            className="mt-auto"
            onClick={() => {
              history.push(`/visual-abstract/${offer.artifactId}/preview`);
            }}
          >
            {t("Open visual abstract")}
          </Button>
        </>
      );
    case "ineligible":
      return (
        <IneligibleOffer
          frame={frame}
          articleId={article.id}
          roomId={roomId}
          reason={offer.reason}
          onChooseAnother={onChooseAnother}
        />
      );
    default:
      throw new Error(offer satisfies never);
  }
}

function GenerateOffer({
  frame,
  article,
  roomId,
  onRequested,
}: {
  frame: SheetFrame;
  article: Article;
  roomId: string | null;
  onRequested: (entryKey: string | null) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const history = useHistory();
  const { isRequesting, request } = useRequestVisualAbstract();
  // Only the offer is an impression, as on the article page's pill: the
  // other branches are not something the reader could have taken.
  useTrackOnMount(
    "visual-abstract-library-offer-shown",
    {
      status: "shown",
      key: `${article.id}:${roomId === null ? "juisci" : roomId}`,
    },
    () => {
      gaEventVisualAbstractCtaShown({
        articleId: article.id,
        roomId,
        affordance: "generate",
        trigger: "library",
      });
    }
  );

  return (
    <>
      {roomId === null ? null : <RoomBranding frame={frame} roomId={roomId} />}
      <SheetFact
        frame={frame}
        icon={<IconPresentationAnalytics className="size-4.5" />}
      >
        {frame === "side-panel"
          ? t(
              "Ready in about a minute. You can leave this page. We'll notify you when it's ready."
            )
          : t(
              "Ready in about a minute. You can leave this screen, we will notify you."
            )}
      </SheetFact>
      <Button
        variant="primary"
        size={frame === "side-panel" ? "lg" : "md"}
        type="button"
        className="mt-auto"
        // A second tap would send a second request and step back twice,
        // leaving the library behind.
        disabled={isRequesting(article.id)}
        onClick={() => {
          // The history entry this click belongs to (see
          // `returnToLibraryIfStillHere`). Absent on the document's first entry.
          const entryKey =
            history.location.key === undefined ? null : history.location.key;
          void request({
            articleId: article.id,
            articleTitle: article.title,
            roomId,
          }).then((outcome) => {
            if (outcome === "requested") onRequested(entryKey);
          });
        }}
      >
        {t("Generate visual abstract")}
      </Button>
    </>
  );
}

function IneligibleOffer({
  frame,
  articleId,
  roomId,
  reason,
  onChooseAnother,
}: {
  frame: SheetFrame;
  articleId: string;
  roomId: string | null;
  reason: IneligibleReason;
  onChooseAnother: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  useTrackOnMount(
    "visual-abstract-library-refusal-shown",
    { status: "shown", key: articleId },
    () => {
      gaEventVisualAbstractUnavailable({ articleId, roomId, reason });
    }
  );
  return (
    <>
      <SheetFact frame={frame} icon={<IconLock className="size-4.5" />}>
        <IneligibleReasonText reason={reason} />
      </SheetFact>
      <Button
        variant="primary"
        size={frame === "side-panel" ? "lg" : "md"}
        type="button"
        className="mt-auto"
        onClick={onChooseAnother}
      >
        {t("Choose another article")}
      </Button>
    </>
  );
}

function RoomBranding({
  frame,
  roomId,
}: {
  frame: SheetFrame;
  roomId: string;
}): React.ReactElement | null {
  const { t } = useTranslation();
  const { roomTextLang } = useActiveLang();
  const roomsQuery = useApiQuery("/webapp/private/rooms", { durable: true });
  if (roomsQuery.state !== "success") return null;
  const room = roomsQuery.body.rooms.find(
    (candidate) => candidate.id === roomId
  );
  if (room === undefined)
    throw new Error(
      `Invariant: room ${roomId} is not one of the reader's rooms`
    );
  const roomName = getRoomName(room, roomTextLang);

  // The chip the poster's header will carry, in the colour the generator gives
  // it: the room's own, or the Juisci green for a room that has none.
  if (frame === "side-panel")
    return (
      <div className="flex flex-col gap-1.5">
        <span className="font-dm-sans text-footnote/4.5 font-semibold text-ds-slate-500">
          {t("Branding")}
        </span>
        <div className="flex flex-col items-start gap-2 rounded-xl border border-solid border-ds-slate-100 bg-ds-slate-50 px-3 py-2.5">
          <span
            className="inline-flex max-w-full items-center gap-1 rounded-md py-0.75 pr-2 pl-1 text-white"
            style={{
              backgroundColor:
                room.primaryColor === null
                  ? "var(--color-ds-mint-800)"
                  : room.primaryColor,
            }}
          >
            <img
              alt=""
              src={getRoomLogoUrl(room)}
              className="size-4 shrink-0 rounded-sm bg-ds-cream object-contain"
            />
            <span className="truncate font-dm-sans text-ui-caption font-bold">
              {roomName}
            </span>
          </span>
          <p className="m-0 font-dm-sans text-footnote/4.5 text-ds-slate-900">
            {t("Room selected: the poster carries the {{roomName}} logo.", {
              roomName,
            })}
          </p>
        </div>
      </div>
    );

  return (
    <div className="flex flex-col gap-1.5">
      <span className="font-dm-sans text-ui-note font-semibold text-ds-slate-500">
        {t("Branding")}
      </span>
      <div className="flex items-center gap-2.5 rounded-2xl border border-solid border-ds-slate-100 bg-ds-cream px-3 py-2.5">
        <img
          alt={roomName}
          src={getRoomLogoUrl(room)}
          className="size-8 shrink-0 rounded-lg bg-white object-contain"
        />
        <p className="m-0 font-dm-sans text-ui-note font-medium text-ds-slate-700">
          {t("Room selected: the poster carries the {{roomName}} logo.", {
            roomName,
          })}
        </p>
      </div>
    </div>
  );
}

function SheetFact({
  frame,
  icon,
  children,
}: {
  frame: SheetFrame;
  icon: React.ReactNode;
  children: React.ReactNode;
}): React.ReactElement {
  if (frame === "side-panel")
    return (
      <div className="flex items-start gap-2.5 text-ds-slate-900">
        <span className="flex shrink-0 text-ds-mint-800">{icon}</span>
        <p className="m-0 font-dm-sans text-sm">{children}</p>
      </div>
    );
  return (
    <div className="flex items-start gap-2.5 text-ds-slate-700">
      <span className="mt-0.5 flex shrink-0">{icon}</span>
      <p className="m-0 font-dm-sans text-ui-body font-medium">{children}</p>
    </div>
  );
}

function SheetLoading(): React.ReactElement {
  return (
    <div className="flex justify-center py-6">
      <IconLoader2 className="size-6 animate-spin text-ds-slate-400" />
    </div>
  );
}

/** One sentence per refusal, each a literal key so the i18n scanner sees it. */
function IneligibleReasonText({
  reason,
}: {
  reason: IneligibleReason;
}): React.ReactElement {
  const { t } = useTranslation();
  switch (reason) {
    case "not_open_access":
      return (
        <>
          {t(
            "This article is not open access. A visual abstract reuses the full text, and the publisher does not allow it."
          )}
        </>
      );
    case "license_missing_or_unknown":
      return (
        <>
          {t(
            "The license of this article does not allow its full text to be reused."
          )}
        </>
      );
    case "doi_unresolved":
      return (
        <>
          {t(
            "This article has no DOI we could verify, so its source cannot be traced."
          )}
        </>
      );
    case "full_text_unavailable":
      return (
        <>{t("Juisci does not have the full text of this article yet.")}</>
      );
    case "source_type_unsupported":
      return (
        <>
          {t(
            "Visual abstracts are not available for this type of publication yet."
          )}
        </>
      );
    case "language_not_enabled":
      return (
        <>
          {t(
            "Visual abstracts are available in English and French only. Switch the app to one of them to generate one."
          )}
        </>
      );
    default:
      throw new Error(reason satisfies never);
  }
}
